import React from "react";
import styled from "styled-components";
import { Braces, ChevronRight, Code2, Cpu, TerminalSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CODE_PRACTICE_LANGUAGES } from "../data/codePracticeData";

const Page = styled.main`
  min-height: 100vh;
  padding: 110px 20px 90px;
  color: #e5e7eb;
  background:
    radial-gradient(900px at 12% 10%, rgba(245, 158, 11, 0.14), transparent 58%),
    radial-gradient(900px at 85% 12%, rgba(56, 189, 248, 0.14), transparent 60%),
    linear-gradient(180deg, #020617 0%, #04111f 100%);
`;

const Shell = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Hero = styled.section`
  padding: 34px;
  border-radius: 30px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 0.82)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 32%);
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.35);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 0.8fr);
  gap: 24px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  font-size: clamp(38px, 5vw, 62px);
  line-height: 1.04;
  color: #f8fafc;
`;

const Accent = styled.span`
  color: #f59e0b;
`;

const Sub = styled.p`
  margin: 18px 0 0;
  max-width: 740px;
  color: #94a3b8;
  font-size: 17px;
  line-height: 1.85;
`;

const ProofGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 24px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const ProofCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.1);
`;

const ProofValue = styled.div`
  color: #f8fafc;
  font-size: 1.8rem;
  font-weight: 1000;
`;

const ProofLabel = styled.div`
  margin-top: 8px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
`;

const HeroSide = styled.div`
  padding: 24px;
  border-radius: 26px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  display: grid;
  gap: 14px;
`;

const SideTitle = styled.div`
  color: #f8fafc;
  font-size: 1.2rem;
  font-weight: 900;
`;

const SideCard = styled.div`
  padding: 16px;
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SideLabel = styled.div`
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SideText = styled.div`
  margin-top: 8px;
  color: #cbd5e1;
  font-size: 14px;
  line-height: 1.7;
`;

const Section = styled.section`
  margin-top: 26px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 1.8rem;
`;

const SectionSub = styled.p`
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
  max-width: 760px;
`;

const Grid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const LanguageCard = styled.button`
  padding: 22px;
  border-radius: 26px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    radial-gradient(circle at top right, ${({ $glow }) => $glow}, transparent 42%),
    rgba(9, 16, 28, 0.92);
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ $accent }) => `${$accent}66`};
    box-shadow: 0 22px 50px rgba(2, 6, 23, 0.24);
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
`;

const Badge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $accent }) => $accent};
  background: ${({ $glow }) => $glow};
  border: 1px solid ${({ $accent }) => `${$accent}33`};
`;

const CardTitle = styled.div`
  color: #f8fafc;
  font-size: 1.3rem;
  font-weight: 900;
`;

const CardText = styled.p`
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.75;
`;

const CardMeta = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaPill = styled.div`
  padding: 8px 11px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
`;

const CardCta = styled.div`
  margin-top: 18px;
  color: #f8fafc;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 900;
`;

export default function CodePracticePage() {
  const navigate = useNavigate();

  return (
    <Page>
      <Shell>
        <Hero>
          <HeroGrid>
            <div>
              <Eyebrow>
                <TerminalSquare size={14} />
                Code practice
              </Eyebrow>
              <Title>
                Open a clean <Accent>code workspace</Accent> for the language you want to practice
              </Title>
              <Sub>
                Pick a language and jump into a clean editor with an output
                panel. This is built for focused coding practice without extra
                clutter, random prompts, or noisy setup.
              </Sub>

              <ProofGrid>
                <ProofCard>
                  <ProofValue>5</ProofValue>
                  <ProofLabel>Language workspaces ready for clean daily code practice.</ProofLabel>
                </ProofCard>
                <ProofCard>
                  <ProofValue>2</ProofValue>
                  <ProofLabel>Panels only: editor on one side and output on the other.</ProofLabel>
                </ProofCard>
                <ProofCard>
                  <ProofValue>1</ProofValue>
                  <ProofLabel>Focused coding surface to stay close to code instead of only watching tutorials.</ProofLabel>
                </ProofCard>
              </ProofGrid>
            </div>

            <HeroSide>
              <SideTitle>How this helps</SideTitle>
              <SideCard>
                <SideLabel>Clean workspace</SideLabel>
                <SideText>
                  Start typing immediately in a clean environment without
                  predefined tasks getting in the way.
                </SideText>
              </SideCard>
              <SideCard>
                <SideLabel>Editor + output</SideLabel>
                <SideText>
                  Each language opens its own coding workspace with a left
                  editor and a right output panel.
                </SideText>
              </SideCard>
              <SideCard>
                <SideLabel>Built for repetition</SideLabel>
                <SideText>
                  You do not need a huge setup to practice. Just open a language
                  and start writing code.
                </SideText>
              </SideCard>
            </HeroSide>
          </HeroGrid>
        </Hero>

        <Section>
          <SectionTitle>Choose your language</SectionTitle>
          <SectionSub>
            Start with the language you are learning right now. Each page gives
            you a clean compiler-style environment designed for direct code
            practice.
          </SectionSub>

          <Grid>
            {CODE_PRACTICE_LANGUAGES.map((language) => (
              <LanguageCard
                key={language.slug}
                $accent={language.accent}
                $glow={language.glow}
                onClick={() => navigate(`/code-practice/${language.slug}`)}
              >
                <CardTop>
                  <div>
                    <CardTitle>{language.label}</CardTitle>
                    <CardText>{language.description}</CardText>
                  </div>
                  <Badge $accent={language.accent} $glow={language.glow}>
                    <Code2 size={20} />
                  </Badge>
                </CardTop>

                <CardMeta>
                  <MetaPill>
                    <Braces size={12} style={{ marginRight: 6 }} />
                    Clean editor
                  </MetaPill>
                  <MetaPill>
                    <Cpu size={12} style={{ marginRight: 6 }} />
                    {language.runMode === "browser" ? "In-browser output" : "Workspace mode"}
                  </MetaPill>
                </CardMeta>

                <CardCta>
                  Open workspace
                  <ChevronRight size={15} />
                </CardCta>
              </LanguageCard>
            ))}
          </Grid>
        </Section>
      </Shell>
    </Page>
  );
}
